import { useCallback, useState } from 'react';
import { Image } from '../../../ui/media/Image';
import { isBloggerBusiness } from '../../../utils/accessService';
import { addProtocol } from '../../../utils/addProtocol';
import { buildImageSource, buildImageSourceSet } from '../../../utils/buildImageSource';
import { getDomainFromUrl } from '../../../utils/getDomainFromUrl/getDomainFromUrl';
import { useTranslation } from '../../../utils/useTranslation';
import { IconArrowDiagonalRight } from '../../icon/IconArrowDiagonalRight';
import { IconClose } from '../../icon/IconClose';
import { IconProduct } from '../../icon/IconProduct';
import { Button } from '../../input/Button';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { MediaCover } from '../../media/MediaCover';
import { Price } from '../../price/Price';
import { ProductDomain } from '../../product/ProductDomain/ProductDomain';
import { Typography } from '../../typography/Typography';
import { ProductRemoveForm } from '../ProductRemoveForm/ProductRemoveForm';
import { PRODUCT_CARD_RATIO } from './constants';
import { RemoveButton, Title } from './styles';
import { ProductCardProps } from './typings';

export const ProductCard = ({
    product: { oldPriceInfo, priceInfo, content, title, url, originalUrl, id, fintechDiscount },
    business,
    contentHeight,
    onProductClick,
    onProductRemove,
    fintechDiscountOptions,
    hideOldPrice,
    showOldPriceBadge,
    canRemoveProduct,
}: ProductCardProps) => {
    const t = useTranslation();
    const media = content[0]?.media;
    const [visibleRemoveForm, setVisibleRemoveForm] = useState(false);
    const onClickRemoveButton = () => setVisibleRemoveForm(true);
    const onCloseRemoveForm = useCallback(() => setVisibleRemoveForm(false), []);
    const onClickRemoveForm = useCallback(() => {
        onProductRemove(id);
        onCloseRemoveForm();
    }, [id, onProductRemove, onCloseRemoveForm]);

    const domain = isBloggerBusiness(business?.type) ? getDomainFromUrl(originalUrl) : undefined;

    return (
        <Box>
            <Box
                backgroundColor="var(--control-secondary-background)"
                borderRadius={3}
                overflow="hidden"
                position="relative"
            >
                <Box
                    as="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    textDecoration="none"
                    href={addProtocol(url)}
                    onClick={onProductClick}
                >
                    <MediaCover
                        ratio={PRODUCT_CARD_RATIO}
                        overflow="hidden"
                        borderRadius="24px"
                        border="solid 4px transparent"
                        backgroundColor="unset"
                    >
                        <Flex
                            alignItems="center"
                            justifyContent="center"
                            backgroundColor="var(--always-white)"
                        >
                            {media ? (
                                <Image
                                    objectFit="contain"
                                    width="100%"
                                    height="100%"
                                    src={buildImageSource(media)}
                                    srcSet={buildImageSourceSet(media)}
                                />
                            ) : (
                                <IconProduct
                                    size={72}
                                    color="var(--control-secondary-background)"
                                />
                            )}
                        </Flex>
                    </MediaCover>
                    <Flex p={1} direction="column" minHeight={contentHeight}>
                        <Title variant="body2" weight="regular">
                            {t(title)}
                        </Title>
                        <Price
                            priceInfo={priceInfo}
                            oldPriceInfo={oldPriceInfo}
                            fintechDiscount={fintechDiscount}
                            variant="body"
                            fintechDiscountOptions={fintechDiscountOptions}
                            hideOldPrice={hideOldPrice}
                            showOldPriceBadge={showOldPriceBadge}
                        />
                        {domain ? <ProductDomain pt={0.5} domain={domain} /> : null}
                        <Button color="secondary" size="m" mt="auto" width="100%">
                            {t('На сайт')}
                            <IconArrowDiagonalRight size={16} />
                        </Button>
                    </Flex>
                </Box>
                {canRemoveProduct ? (
                    <RemoveButton variant="filled" onClick={onClickRemoveButton} size="xs">
                        <IconClose size={16} />
                    </RemoveButton>
                ) : null}
                {visibleRemoveForm ? (
                    <ProductRemoveForm onClose={onCloseRemoveForm} onRemove={onClickRemoveForm} />
                ) : null}
            </Box>
        </Box>
    );
};
